/*
 * Seasaltco. storefront behaviour.
 *
 * Ported from the design handoff's prototype/app.js, with the demo cart
 * (localStorage) replaced by Shopify's Cart AJAX API. Every handler is
 * delegated from document, so sections the theme editor re-renders keep
 * working without re-initialising anything.
 *
 * Cart: /cart/add.js and /cart/change.js are called with the "sections"
 * parameter, so each response carries freshly rendered ss-cart-drawer markup
 * (Section Rendering API). That markup replaces the drawer's contents and its
 * data-count updates every [data-ss-cart-count] on the page.
 */
(() => {
  const DRAWER_SECTION = 'ss-cart-drawer';
  const root = document.documentElement;
  const routes = (window.Shopify && window.Shopify.routes) || {};
  const base = (routes.root || '/').replace(/\/?$/, '/');
  const url = (path) => base + path.replace(/^\//, '');

  /* ---------------------------------------------------------- drawer */

  const drawer = () => document.getElementById('ss-cart-drawer');
  let lastFocus = null;

  function openCart() {
    const d = drawer();
    if (!d) return false;
    lastFocus = document.activeElement;
    root.classList.add('ss-cart-open', 'ss-lock');
    requestAnimationFrame(() => d.focus({ preventScroll: true }));
    return true;
  }

  function closeCart() {
    if (!root.classList.contains('ss-cart-open')) return;
    root.classList.remove('ss-cart-open', 'ss-lock');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  function renderCart(html) {
    const d = drawer();
    if (!d || !html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-ss-cart-inner]');
    const current = d.querySelector('[data-ss-cart-inner]');
    if (!next || !current) return;
    current.replaceWith(next);
    const count = next.dataset.count;
    document.querySelectorAll('[data-ss-cart-count]').forEach((el) => (el.textContent = count));
  }

  function cartError(message) {
    const el = drawer() && drawer().querySelector('[data-ss-cart-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  async function post(path, body) {
    const res = await fetch(url(path), {
      method: 'POST',
      headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    return data;
  }

  async function refreshCart() {
    const res = await fetch(url('?sections=' + DRAWER_SECTION));
    const data = await res.json();
    renderCart(data[DRAWER_SECTION]);
  }

  async function changeLine(line, quantity) {
    const d = drawer();
    d && d.classList.add('is-busy');
    try {
      const data = await post('cart/change.js', { line: Number(line), quantity: Number(quantity), sections: DRAWER_SECTION });
      renderCart(data.sections && data.sections[DRAWER_SECTION]);
    } catch (err) {
      cartError(err.message);
    } finally {
      d && d.classList.remove('is-busy');
    }
  }

  async function addToCart(form, submitter) {
    const buyNow = submitter && submitter.hasAttribute('data-ss-buynow');
    const errorEl = form.querySelector('[data-ss-form-error]');
    const buttons = form.querySelectorAll('button[type="submit"], button:not([type])');
    buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
    if (errorEl) errorEl.hidden = true;
    try {
      const body = new FormData(form);
      body.append('sections', DRAWER_SECTION);
      body.append('sections_url', window.location.pathname);
      const data = await post('cart/add.js', body);
      if (buyNow) {
        window.location.href = url('checkout');
        return;
      }
      renderCart(data.sections && data.sections[DRAWER_SECTION]);
      openCart();
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } else {
        window.alert(err.message);
      }
    } finally {
      buttons.forEach((b) => b.removeAttribute('aria-busy'));
    }
  }

  /* Horizon components (quick add on search results) announce cart changes
     with this event; show the result in our drawer. */
  document.addEventListener('shopify:cart:lines-update', () => {
    refreshCart().then(openCart).catch(() => {});
  });

  /* ------------------------------------------------------ mobile menu */

  function toggleMenu(force) {
    const nav = document.getElementById('ss-mnav');
    if (!nav) return;
    const open = typeof force === 'boolean' ? force : nav.hidden;
    nav.hidden = !open;
    root.classList.toggle('ss-lock', open);
    document.querySelectorAll('[data-ss-menu][aria-controls]').forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) {
      const close = nav.querySelector('[data-ss-menu]');
      close && close.focus();
    }
  }

  /* ----------------------------------------------------- before/after */

  function setBA(stage, pct) {
    const v = Math.max(0, Math.min(100, pct));
    stage.style.setProperty('--x', v + '%');
    const knob = stage.querySelector('[role="slider"]');
    if (knob) {
      knob.setAttribute('aria-valuenow', String(Math.round(v)));
      knob.setAttribute('aria-valuetext', Math.round(v) + '% after');
    }
  }

  function baFromPointer(stage, clientX) {
    const r = stage.getBoundingClientRect();
    setBA(stage, ((clientX - r.left) / r.width) * 100);
  }

  let baDrag = null;

  document.addEventListener('pointerdown', (e) => {
    const stage = e.target.closest('[data-ss-ba]');
    if (!stage || e.button > 0) return;
    baDrag = stage;
    stage.setPointerCapture(e.pointerId);
    baFromPointer(stage, e.clientX);
  });

  document.addEventListener('pointermove', (e) => {
    if (baDrag) baFromPointer(baDrag, e.clientX);
  });

  ['pointerup', 'pointercancel'].forEach((type) =>
    document.addEventListener(type, () => {
      baDrag = null;
    })
  );

  /* ----------------------------------------------------------- gallery */

  /* The product gallery is a scroll-snap strip: swipe or trackpad moves it
     natively; thumbnails and arrows scroll it to an index; the thumbnail
     state and counter follow whichever slide is in view. */
  const galleryTrack = (gal) => gal && gal.querySelector('[data-ss-track]');

  function galleryIndex(gal) {
    const track = galleryTrack(gal);
    return track ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) : 0;
  }

  function galleryGo(gal, index, instant) {
    const track = galleryTrack(gal);
    if (!track) return;
    const n = track.children.length;
    const i = Math.max(0, Math.min(n - 1, index));
    track.scrollTo({ left: i * track.clientWidth, behavior: instant ? 'instant' : 'smooth' });
    gallerySync(gal, i);
  }

  function gallerySync(gal, i) {
    const n = galleryTrack(gal).children.length;
    gal.querySelectorAll('[data-ss-thumb]').forEach((b) => {
      const on = Number(b.dataset.ssThumb) === i;
      b.setAttribute('aria-current', String(on));
      if (!on) return;
      /* Keep the active thumbnail in view inside the rail, on either axis,
         without scrolling the page. */
      const rail = b.parentElement;
      if (rail.scrollHeight > rail.clientHeight) {
        const top = b.offsetTop - rail.offsetTop;
        if (top < rail.scrollTop || top + b.offsetHeight > rail.scrollTop + rail.clientHeight) {
          rail.scrollTo({ top: top - 2, behavior: 'smooth' });
        }
      } else if (rail.scrollWidth > rail.clientWidth) {
        rail.scrollTo({ left: b.offsetLeft - rail.offsetLeft - 2, behavior: 'smooth' });
      }
    });
    const prev = gal.querySelector('[data-ss-step-gal="-1"]');
    const next = gal.querySelector('[data-ss-step-gal="1"]');
    if (prev) prev.hidden = i === 0;
    if (next) next.hidden = i === n - 1;
    const count = gal.querySelector('[data-ss-count]');
    if (count) count.textContent = i + 1 + ' / ' + n;
  }

  /* The rail's chevron: shown only when thumbnails overflow; pages down,
     and at the end turns to point up and returns to the top. */
  function railSync(gal) {
    const rail = gal.querySelector('[data-ss-thumbs]');
    const more = gal.querySelector('[data-ss-thumbs-more]');
    if (!rail || !more) return;
    more.hidden = rail.scrollHeight <= rail.clientHeight + 2;
    const atEnd = rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 2;
    more.dataset.dir = atEnd ? 'up' : 'down';
    more.setAttribute('aria-label', atEnd ? 'Back to first images' : 'More images');
  }

  document.addEventListener(
    'scroll',
    (e) => {
      if (e.target.matches && e.target.matches('[data-ss-thumbs]')) {
        railSync(e.target.closest('[data-ss-gallery]'));
        return;
      }
      const track = e.target.closest && e.target.closest('[data-ss-track]');
      if (!track) return;
      clearTimeout(track._ssT);
      track._ssT = setTimeout(() => {
        const gal = track.closest('[data-ss-gallery]');
        gallerySync(gal, galleryIndex(gal));
      }, 80);
    },
    true
  );

  function galleryInit(scope) {
    scope.querySelectorAll('[data-ss-gallery]').forEach((gal) => {
      const start = Number(gal.dataset.ssStart) || 0;
      galleryGo(gal, start, true);
      railSync(gal);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => galleryInit(document));
  } else {
    galleryInit(document);
  }
  document.addEventListener('shopify:section:load', (e) => galleryInit(e.target));
  window.addEventListener('resize', () => document.querySelectorAll('[data-ss-gallery]').forEach(railSync));

  /* Arrow keys step the gallery when it has focus. */
  document.addEventListener('keydown', (e) => {
    const track = e.target.closest && e.target.closest('[data-ss-track]');
    if (!track || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    const gal = track.closest('[data-ss-gallery]');
    galleryGo(gal, galleryIndex(gal) + (e.key === 'ArrowRight' ? 1 : -1));
  });

  /* ------------------------------------------------------------ clicks */

  document.addEventListener('click', (e) => {
    const t = e.target.closest('a, button');
    if (!t) return;

    if (t.hasAttribute('data-ss-cart-open')) {
      if (openCart()) e.preventDefault();
      return;
    }

    if (t.hasAttribute('data-ss-cart-close')) {
      e.preventDefault();
      closeCart();
      return;
    }

    if (t.hasAttribute('data-ss-line')) {
      changeLine(t.dataset.ssLine, t.dataset.ssQty);
      return;
    }

    if (t.hasAttribute('data-ss-menu')) {
      toggleMenu();
      return;
    }

    if (t.closest('#ss-mnav') && t.tagName === 'A') {
      toggleMenu(false);
      return;
    }

    if (t.hasAttribute('data-ss-step')) {
      const input = t.closest('[data-ss-qty-wrap]').querySelector('input');
      const min = Number(input.min) || 1;
      input.value = Math.max(min, (Number(input.value) || min) + Number(t.dataset.ssStep));
      return;
    }

    if (t.hasAttribute('data-ss-thumb')) {
      galleryGo(t.closest('[data-ss-gallery]'), Number(t.dataset.ssThumb));
      return;
    }

    if (t.hasAttribute('data-ss-thumbs-more')) {
      const rail = t.closest('[data-ss-gallery]').querySelector('[data-ss-thumbs]');
      if (t.dataset.dir === 'up') rail.scrollTo({ top: 0, behavior: 'smooth' });
      else rail.scrollBy({ top: rail.clientHeight * 0.8, behavior: 'smooth' });
      return;
    }

    if (t.hasAttribute('data-ss-step-gal')) {
      const gal = t.closest('[data-ss-gallery]');
      galleryGo(gal, galleryIndex(gal) + Number(t.dataset.ssStepGal));
      return;
    }

    if (t.hasAttribute('data-ss-filter')) {
      const scope = t.closest('[data-ss-shop]');
      const f = t.dataset.ssFilter;
      let shown = 0;
      scope.querySelectorAll('[data-ss-filter]').forEach((c) => c.setAttribute('aria-pressed', String(c === t)));
      scope.querySelectorAll('[data-ss-status]').forEach((card) => {
        const hide = f !== 'all' && card.dataset.ssStatus !== f;
        card.hidden = hide;
        if (!hide) shown++;
      });
      const empty = scope.querySelector('[data-ss-shop-empty]');
      if (empty) empty.hidden = shown > 0;
      return;
    }

    if (t.hasAttribute('data-ss-scroll')) {
      const target = document.getElementById(t.dataset.ssScroll);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  });

  /* ------------------------------------------------------------- keys */

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      const nav = document.getElementById('ss-mnav');
      if (nav && !nav.hidden) toggleMenu(false);
      return;
    }

    const knob = e.target.closest && e.target.closest('[data-ss-ba] [role="slider"]');
    if (knob) {
      const stage = knob.closest('[data-ss-ba]');
      const now = Number(knob.getAttribute('aria-valuenow')) || 50;
      const map = { ArrowLeft: now - 5, ArrowDown: now - 5, ArrowRight: now + 5, ArrowUp: now + 5, Home: 0, End: 100 };
      if (e.key in map) {
        e.preventDefault();
        setBA(stage, map[e.key]);
      }
      return;
    }

    /* Keep focus inside the open drawer. */
    if (e.key === 'Tab' && root.classList.contains('ss-cart-open')) {
      const d = drawer();
      const items = [...d.querySelectorAll('a[href], button:not([disabled]), input, [tabindex="0"]')].filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === d)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* Products with more than one variant: reload on the chosen variant so the
     price, image and availability are server-rendered for it. */
  document.addEventListener('change', (e) => {
    const select = e.target.closest && e.target.closest('select[data-ss-variant]');
    if (!select) return;
    const next = new URL(window.location.href);
    next.searchParams.set('variant', select.value);
    window.location.replace(next.toString());
  });

  /* Hero shade picker: the checked swatch sets the variant id and the
     button's label, or disables it when that shade is sold out. */
  document.addEventListener('change', (e) => {
    const radio = e.target.closest && e.target.closest('[data-ss-hero-shade]');
    if (!radio) return;
    const form = radio.closest('[data-ss-hero-form]');
    const id = form.querySelector('[data-ss-hero-id]');
    const button = form.querySelector('[data-ss-hero-button]');
    const name = form.querySelector('[data-ss-hero-shade-name]');
    const shade = radio.dataset.name;
    const available = radio.dataset.available === 'true';
    id.value = radio.value;
    if (name) name.textContent = shade;
    button.disabled = !available;
    button.textContent = available
      ? button.dataset.label + ' — ' + shade + (button.dataset.showPrice === 'true' ? ' · ' + radio.dataset.price : '')
      : 'Sold out — ' + shade;
    const error = form.querySelector('[data-ss-form-error]');
    if (error) error.hidden = true;
  });

  /* Sticky purchase bar: shown while the main Add to bag row is above the
     viewport; its button submits the main form with the Add to bag button
     as submitter, so it behaves exactly like the original. */
  function stickyInit(scope) {
    scope.querySelectorAll('[data-ss-stickybar]').forEach((bar) => {
      const row = document.querySelector('.ss-buy__row');
      if (!row || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(([entry]) => {
        const on = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        bar.classList.toggle('is-on', on);
        bar.setAttribute('aria-hidden', String(!on));
        bar.querySelector('[data-ss-sticky-add]').tabIndex = on ? 0 : -1;
        root.classList.toggle('ss-bar-on', on);
      });
      io.observe(row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => stickyInit(document));
  } else {
    stickyInit(document);
  }

  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-ss-sticky-add]');
    if (!t) return;
    const form = document.querySelector('.ss-buy__form');
    const submit = form && form.querySelector('.ss-buy__row button[type="submit"]');
    if (submit) form.requestSubmit(submit);
  });

  /* ------------------------------------------------------------ forms */

  document.addEventListener('submit', (e) => {
    const form = e.target;

    if (form.hasAttribute('data-ss-add')) {
      e.preventDefault();
      addToCart(form, e.submitter);
      return;
    }

    if (form.hasAttribute('data-ss-pin')) {
      e.preventDefault();
      const input = form.querySelector('input');
      const out = form.querySelector('[data-ss-pin-out]');
      const pin = (input.value || '').trim();
      out.textContent = /^[1-9]\d{5}$/.test(pin)
        ? form.dataset.ssPinOk.replace('[pin]', pin)
        : form.dataset.ssPinBad;
    }
  });
})();

/*
 * Policy pages. Shopify renders the policy body, so its h2 headings get ids
 * here and fill the "Jump to a section" list (snippets/ss-policy.liquid),
 * with the section in view marked. A final h2 containing "contact" is
 * wrapped, with what follows it, in the dark contact panel.
 */
(() => {
  const prose = document.querySelector('.ss-policy .ss-prose');
  const toc = document.querySelector('[data-ss-toc]');
  if (!prose || !toc) return;

  const headings = [...prose.querySelectorAll('h2')];
  if (headings.length < 4) return;

  const last = headings[headings.length - 1];
  if (/contact/i.test(last.textContent)) {
    const panel = document.createElement('div');
    panel.className = 'ss-policy-contact';
    last.before(panel);
    let node = last;
    while (node) {
      const next = node.nextSibling;
      panel.append(node);
      node = next;
    }
  }

  const list = toc.querySelector('ol');
  const links = headings.map((h) => {
    const label = h.textContent.replace(/^\s*\d+\.\s*/, '').trim();
    if (!h.id) {
      h.id = label.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = '#' + h.id;
    a.textContent = label;
    li.append(a);
    list.append(li);
    return a;
  });
  toc.hidden = false;

  const mark = (i) => links.forEach((a, j) => a.setAttribute('aria-current', i === j ? 'true' : 'false'));
  const onScroll = () => {
    let current = 0;
    headings.forEach((h, i) => {
      if (h.getBoundingClientRect().top < 160) current = i;
    });
    mark(current);
  };
  // The page scrolls inside .page-wrapper, not the window; capture catches
  // scroll events from whichever element is scrolling.
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  onScroll();
})();
